import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Sync as SyncIcon, Hub as IntegracaoIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

interface Regra { categoria: string; padrao: string }
interface ResultadoSync { total: number; criados: number; actualizados: number; ligados: number; desactivados: number; comQuantidade?: number; erro?: string }
interface Config {
  tipo: string; activo: boolean; verificarNaCompra: boolean; config: Record<string, string>; regras: Regra[];
  ultimaSyncEm: string | null; ultimaSync: ResultadoSync | null; produtosLigados?: number; produtosActivos?: number;
  temChaves: boolean; chaveTermina: string | null;
}
interface Tipo {
  tipo: string; nome: string; descricao: string; leitura: boolean;
  campos: { chave: string; rotulo: string; ajuda?: string; obrigatorio: boolean }[];
  credenciais: { obrigatorias: boolean; chave: string; segredo: string; ajuda: string } | null;
}
interface Execucao { id: string; origem: string; inicio: string; resultado: ResultadoSync | null; erro: string | null }

/** «Categoria = padrão» por linha ⇄ regras. O «=» que separa é o primeiro. */
const regrasParaTexto = (r: Regra[]) => r.map((x) => `${x.categoria} = ${x.padrao}`).join('\n');
function textoParaRegras(texto: string): { regras: Regra[]; linhaErrada?: number } {
  const regras: Regra[] = [];
  const linhas = texto.split('\n');
  for (let i = 0; i < linhas.length; i++) {
    const l = linhas[i].trim();
    if (!l) continue;
    const k = l.indexOf('=');
    if (k <= 0 || k === l.length - 1) return { regras, linhaErrada: i + 1 };
    regras.push({ categoria: l.slice(0, k).trim(), padrao: l.slice(k + 1).trim() });
  }
  return { regras };
}

/**
 * Ligar uma loja do Pagali Shop ao sistema do comerciante (WooCommerce e os
 * que vierem). Backend: api/shop-integracoes.ts, services/integracoes/.
 * Só leitura: a Pagali não escreve no sistema do comerciante.
 */
const IntegracoesDialog: React.FC<{ store: { id: string; name: string } | null; onClose: () => void }> = ({ store, onClose }) => {
  const { t } = useTranslation();
  const [tipos, setTipos] = useState<Tipo[]>([]);
  const [cfg, setCfg] = useState<Config | null>(null);
  const [execucoes, setExecucoes] = useState<Execucao[]>([]);
  const [tipo, setTipo] = useState('');
  const [campos, setCampos] = useState<Record<string, string>>({});
  const [activo, setActivo] = useState(true);
  const [verificar, setVerificar] = useState(true);
  const [regras, setRegras] = useState('');
  // As credenciais só se escrevem: o servidor nunca as devolve. Vazias = manter as gravadas.
  const [chave, setChave] = useState('');
  const [segredo, setSegredo] = useState('');
  const [removerChaves, setRemoverChaves] = useState(false);
  const [aCarregar, setACarregar] = useState(false);
  const [aGravar, setAGravar] = useState(false);
  const [aSincronizar, setASincronizar] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  // A chave da API de Catálogo só se vê na resposta que a cria.
  const [chaveGerada, setChaveGerada] = useState<string | null>(null);
  const [aGerar, setAGerar] = useState(false);

  const def = tipos.find((x) => x.tipo === tipo) || null;

  const carregar = async (id: string) => {
    setACarregar(true); setErro(null);
    try {
      const [rt, rc, re] = await Promise.all([
        api.get('/admin/shop/integracoes/tipos'),
        api.get(`/admin/shop/integracoes/${id}`),
        api.get(`/admin/shop/integracoes/${id}/execucoes?limit=5`),
      ]);
      const ts: Tipo[] = rt.data?.data || [];
      const c: Config | null = rc.data?.data || null;
      setTipos(ts); setCfg(c); setExecucoes(re.data?.data || []);
      setTipo(c?.tipo || ts[0]?.tipo || '');
      setCampos(c?.config || {}); setActivo(c ? c.activo : true); setVerificar(c ? c.verificarNaCompra : true);
      setRegras(regrasParaTexto(c?.regras || []));
      setChave(''); setSegredo(''); setRemoverChaves(false);
    } catch (e: any) {
      setErro(e?.response?.data?.error || e.message);
    } finally { setACarregar(false); }
  };

  const gerarChave = async () => {
    if (!store) return;
    setAGerar(true); setErro(null);
    try {
      const r = await api.post(`/admin/shop/integracoes/${store.id}/chave-catalogo`);
      setChaveGerada(r.data?.data?.apiKey || null);
    } catch (e: any) {
      setErro(e?.response?.data?.error || e.message);
    } finally { setAGerar(false); }
  };

  useEffect(() => {
    setAviso(null); setErro(null); setCfg(null); setChaveGerada(null);
    if (store) carregar(store.id);
  }, [store]);

  const gravar = async () => {
    if (!store || !def) return;
    const { regras: lista, linhaErrada } = textoParaRegras(regras);
    if (linhaErrada) { setErro(String(t('shop.woo.regraInvalida', { linha: linhaErrada }))); return; }
    setAGravar(true); setErro(null); setAviso(null);
    try {
      const r = await api.put(`/admin/shop/integracoes/${store.id}`, {
        tipo, config: campos, activo, verificarNaCompra: verificar, regras: lista,
        ...(removerChaves ? { removerCredenciais: true } : chave || segredo ? { credenciais: { key: chave, secret: segredo } } : {}),
      });
      setAviso(String(t('shop.woo.gravado', { total: r.data?.data?.produtosNoSite ?? '?' })));
      await carregar(store.id);
    } catch (e: any) {
      setErro(e?.response?.data?.error || e.message);
    } finally { setAGravar(false); }
  };

  const sincronizar = async () => {
    if (!store) return;
    setASincronizar(true); setErro(null); setAviso(null);
    try {
      const r = await api.post(`/admin/shop/integracoes/${store.id}/sincronizar`);
      setAviso(String(t('shop.woo.sincronizado', { ...r.data.data })));
    } catch (e: any) {
      setErro(e?.response?.data?.error || e.message);
    } finally {
      setASincronizar(false);
      await carregar(store.id);
    }
  };

  const ocupado = aGravar || aSincronizar || aCarregar;
  const camposOk = !!def && def.campos.every((c) => !c.obrigatorio || (campos[c.chave] || '').trim());
  // Mudar de tipo apaga as credenciais do tipo anterior (o servidor faz o mesmo).
  const outroTipo = !!cfg && cfg.tipo !== tipo;
  return (
    <Dialog open={!!store} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IntegracaoIcon /> {t('shop.woo.titulo')} — {store?.name}
      </DialogTitle>
      <DialogContent dividers>
        {aCarregar ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box> : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">{t('shop.woo.intro')}</Typography>
            {erro && <Alert severity="error" onClose={() => setErro(null)}>{erro}</Alert>}
            {aviso && <Alert severity="success" onClose={() => setAviso(null)}>{aviso}</Alert>}
            <TextField select fullWidth label={t('shop.woo.tipo')} value={tipo} onChange={(e) => setTipo(e.target.value)}
              helperText={def?.descricao}>
              {tipos.map((x) => <MenuItem key={x.tipo} value={x.tipo}>{x.nome}</MenuItem>)}
            </TextField>
            {def?.campos.map((c) => (
              <TextField
                key={c.chave} fullWidth required={c.obrigatorio} label={c.rotulo} helperText={c.ajuda}
                value={campos[c.chave] || ''} onChange={(e) => setCampos({ ...campos, [c.chave]: e.target.value })}
              />
            ))}
            <Box>
              <FormControlLabel control={<Switch checked={activo} onChange={(e) => setActivo(e.target.checked)} />} label={t('shop.woo.activo')} />
              {def?.leitura && (
                <FormControlLabel control={<Switch checked={verificar} onChange={(e) => setVerificar(e.target.checked)} />} label={t('shop.woo.verificar')} />
              )}
            </Box>
            {def?.credenciais && (
              <Box>
                <Typography variant="subtitle2">{t('shop.woo.chaves')}</Typography>
                <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
                  {cfg?.temChaves && !outroTipo ? t('shop.woo.chavesGravadas', { fim: cfg.chaveTermina }) : def.credenciais.ajuda}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    sx={{ flex: '1 1 260px' }} label={def.credenciais.chave} value={chave} autoComplete="off"
                    disabled={removerChaves} onChange={(e) => setChave(e.target.value.trim())}
                  />
                  <TextField
                    sx={{ flex: '1 1 260px' }} label={def.credenciais.segredo} type="password" value={segredo}
                    autoComplete="new-password" disabled={removerChaves} onChange={(e) => setSegredo(e.target.value.trim())}
                  />
                </Box>
                {cfg?.temChaves && !outroTipo && (
                  <FormControlLabel
                    control={<Switch checked={removerChaves} onChange={(e) => setRemoverChaves(e.target.checked)} />}
                    label={t('shop.woo.removerChaves')}
                  />
                )}
              </Box>
            )}
            {def && !def.leitura && (
              <Alert severity="info" variant="outlined">
                <Typography variant="body2" sx={{ mb: 1 }}>{t('shop.woo.apiAjuda')}</Typography>
                <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', fontSize: 12, m: 0, whiteSpace: 'pre-wrap' }}>
                  {'PUT   /v1/catalogo/lojas/<loja>/produtos   { "produtos": [...], "completo": false }\nPATCH /v1/catalogo/lojas/<loja>/stock      { "itens": [{ "id": "...", "quantidade": 3 }] }\nGET   /v1/catalogo/lojas/<loja>/produtos\nHeader: X-API-Key: pk_…'}
                </Typography>
                {cfg?.tipo === def.tipo ? (
                  <Box sx={{ mt: 1.5 }}>
                    {chaveGerada ? (
                      <Alert severity="warning">
                        <Typography variant="body2" sx={{ mb: 1 }}>{t('shop.woo.chaveSoAgora')}</Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{chaveGerada}</Typography>
                          <Button size="small" onClick={() => navigator.clipboard?.writeText(chaveGerada)}>{t('shop.woo.copiar')}</Button>
                        </Box>
                      </Alert>
                    ) : (
                      <Button size="small" variant="outlined" onClick={gerarChave} disabled={aGerar}
                        startIcon={aGerar ? <CircularProgress size={14} /> : null}>
                        {t('shop.woo.gerarChave')}
                      </Button>
                    )}
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">{t('shop.woo.gravarPrimeiro')}</Typography>
                )}
              </Alert>
            )}
            {def?.leitura && (
              <TextField
                fullWidth multiline minRows={4} maxRows={14} label={t('shop.woo.regras')} value={regras}
                helperText={t('shop.woo.regrasAjuda')} onChange={(e) => setRegras(e.target.value)}
                placeholder={'Telemóveis e Tablets = galaxy|telemove|tablet\nCozinha = cozinha|panela'}
                slotProps={{ input: { sx: { fontFamily: 'monospace', fontSize: 13 } } }}
              />
            )}
            {cfg && (
              <Alert severity={cfg.ultimaSync?.erro ? 'warning' : 'info'} variant="outlined">
                <Typography variant="body2">
                  <strong>{t('shop.woo.ultima')}:</strong>{' '}
                  {cfg.ultimaSyncEm ? new Date(cfg.ultimaSyncEm).toLocaleString() : t('shop.woo.nunca')}
                  {cfg.ultimaSync && !cfg.ultimaSync.erro && ` — ${t('shop.woo.sincronizado', { ...cfg.ultimaSync })}`}
                  {cfg.ultimaSync?.erro && ` — ${cfg.ultimaSync.erro}`}
                </Typography>
                <Typography variant="body2">{t('shop.woo.ligados', { ligados: cfg.produtosLigados ?? 0, activos: cfg.produtosActivos ?? 0 })}</Typography>
                {cfg.ultimaSync?.comQuantidade != null && (
                  <Typography variant="body2">{t('shop.woo.comQuantidade', { n: cfg.ultimaSync.comQuantidade })}</Typography>
                )}
              </Alert>
            )}
            {execucoes.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{t('shop.woo.historico')}</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('shop.woo.quando')}</TableCell>
                      <TableCell>{t('shop.woo.origem')}</TableCell>
                      <TableCell>{t('shop.woo.resultado')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {execucoes.map((x) => (
                      <TableRow key={x.id}>
                        <TableCell>{new Date(x.inicio).toLocaleString()}</TableCell>
                        <TableCell>{t(`shop.woo.origem_${x.origem}`, { defaultValue: x.origem })}</TableCell>
                        <TableCell sx={{ color: x.erro ? 'error.main' : undefined }}>
                          {x.erro || (x.resultado ? t('shop.woo.sincronizado', { ...x.resultado }) : '…')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          onClick={sincronizar} disabled={!cfg || !def?.leitura || outroTipo || ocupado}
          startIcon={aSincronizar ? <CircularProgress size={16} /> : <SyncIcon />}
        >
          {aSincronizar ? t('shop.woo.aSincronizar') : t('shop.woo.sincronizar')}
        </Button>
        <Button
          variant="contained" onClick={gravar} disabled={!camposOk || ocupado}
          startIcon={aGravar ? <CircularProgress size={16} /> : null}
        >
          {aGravar ? t('common.saving') : t('shop.woo.gravar')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IntegracoesDialog;
